// Loads a project bundle and runs the engine in the browser (rule: calc engine in the front end).
import { useQuery } from "convex/react";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";
import { toProjectData, currentPeriodIdx, lastClosedIdx, treeOrder, type StoredBundle } from "@/shared/model";
import { prepare, type Prepared, type ProjectData } from "@/engine/compute";
import { validateDeliverable, type Issue } from "@/engine/validate";
import { BREAKDOWNS, type Breakdown, type Chain } from "@/engine/types";

type Bundle = NonNullable<ReturnType<typeof useBundleQuery>>;
function useBundleQuery(projectId: Id<"projects">) {
  return useQuery(api.projects.bundle, { projectId });
}

export interface ProjectCtx {
  b: Bundle;
  project: Bundle["project"];
  canEdit: boolean;
  isAdmin: boolean;
  data: ProjectData;
  prep: Prepared;
  chains: Map<string, Chain>;
  periods: Doc<"periods">[];
  currentIdx: number;
  lastClosedIdx: number;
  issues: Map<string, Issue[]>;
  issueCount: { errors: number; warnings: number };
  nodes: Record<Breakdown, Doc<"breakdownNodes">[]>;
  known: Partial<Record<Breakdown, Set<string>>>;
  unresolved: string[];
  /** Breakdowns switched on by the setup toggles (design flag 9). */
  activeBreakdowns: Breakdown[];
}

const Ctx = createContext<ProjectCtx | null>(null);

export function useProject(): ProjectCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useProject outside a project");
  return c;
}

export function ProjectProvider({ projectId, children, fallback }: { projectId: Id<"projects">; children: ReactNode; fallback: ReactNode }) {
  const b = useBundleQuery(projectId);
  const value = useMemo<ProjectCtx | null>(() => {
    if (!b) return null;
    const stored: StoredBundle = {
      periods: b.periods,
      chains: b.chains,
      deliverables: b.deliverables,
      changeOrders: b.changeOrders,
      actuals: b.actuals,
      indirects: b.indirects,
      settings: b.project.settings,
    };
    const { data, chains, unresolved } = toProjectData(stored);
    const nodes = Object.fromEntries(BREAKDOWNS.map((t) => [t, treeOrder(b.nodes.filter((n) => n.type === t))])) as ProjectCtx["nodes"];
    data.codeOrder = Object.fromEntries(BREAKDOWNS.map((t) => [t, new Map(nodes[t].map((n, i) => [n.code, i]))]));
    const prep = prepare(data);
    const periods = [...b.periods].sort((x, y) => x.number - y.number);
    const known = Object.fromEntries(BREAKDOWNS.map((t) => [t, new Set(nodes[t].map((n) => n.code))])) as ProjectCtx["known"];
    const cur = currentPeriodIdx(periods);
    const issues = new Map<string, Issue[]>();
    let errors = 0;
    let warnings = 0;
    for (const d of data.deliverables) {
      const is = validateDeliverable(d, { periods: data.periods, chains, currentIdx: cur >= 0 ? cur : undefined, knownCodes: known });
      if (is.length) issues.set(d.id, is);
      for (const i of is) i.severity === "error" ? errors++ : warnings++;
    }
    const o = b.project.options;
    const activeBreakdowns = BREAKDOWNS.filter((t) => !(t === "obs" && o.noObs) && !(t === "pbs" && o.noPbs) && !(t === "wbs" && o.noWbs));
    return {
      b,
      project: b.project,
      canEdit: b.role !== "viewer",
      isAdmin: b.role === "admin",
      data,
      prep,
      chains,
      periods,
      currentIdx: cur,
      lastClosedIdx: lastClosedIdx(periods),
      issues,
      issueCount: { errors, warnings },
      nodes,
      known,
      unresolved,
      activeBreakdowns,
    };
  }, [b]);
  if (!value) return <>{fallback}</>;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const BD_LABEL: Record<Breakdown, string> = { wbs: "WBS", pbs: "PBS", obs: "OBS", dbs: "DBS", cbs: "CBS", sbs: "SBS/XBS" };
